import { useI18n } from '@platform/i18n';
import { Alert, Skeleton } from '../components/misc';
import { Button } from '../components/button';
import { ErrorState } from '../composites/states';
import { Label } from '../components/label';
import { LazyRichTextEditor } from '../editor/lazy-rich-text-editor';
import { MenuSelect } from '../components/menu-select';
import { plainTextToMailHtml } from '../mail/sanitize';
import { SegmentedControl } from '../components/form-controls';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/tabs';
import { SettingsCard } from '../composites/settings';
import { toast } from '../components/sonner';
import { useAsync } from '../hooks/use-async';
import { useErrorText } from './errors';
import { SignaturePreview } from './signature-editor';
import { useReportEdits } from '../composites/edit-boundary';
import type { RichTextValue } from '../editor/rich-text-editor';
import type { DateTimePickerProps } from './date-time-picker';
import { TriangleAlertIcon } from 'lucide-react';
import { useRef, useState, type ComponentType } from 'react';
import type { MailboxAutomaticReplies as AutomaticReplies, MailboxAutomaticRepliesInput as AutomaticRepliesInput, MailboxAutomaticRepliesAdapter } from './mailbox-settings-types';
type Audience = AutomaticRepliesInput['externalAudience'];
type State = AutomaticRepliesInput['state'];

const HOUR_MS = 3_600_000;

function nativeBounds(data: AutomaticReplies | undefined) {
  return data && 'nativeStartTime' in data ? data : undefined;
}

/** ISO instant → `datetime-local` value in the browser's time zone. */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** `datetime-local` value (browser time zone) → ISO instant; null when empty or invalid. */
function fromLocalInput(local: string): string | null {
  const date = new Date(local);
  return local === '' || Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** Editor value as reply HTML (plain text becomes simple HTML). */
const toHtml = (value: RichTextValue): string => (value.format === 'html' ? value.content : plainTextToMailHtml(value.content));

interface Form {
  state: State;
  start: string;
  end: string;
  internalHtml: string;
  externalHtml: string;
  externalAudience: Audience;
}

const toForm = (data: AutomaticReplies): Form => ({
  state: data.state,
  start: 'nativeStartTime' in data ? (data.nativeStartTime ?? '').replace(' ', 'T') : toLocalInput(data.start),
  end: 'nativeEndTime' in data ? (data.nativeEndTime ?? '').replace(' ', 'T') : toLocalInput(data.end),
  internalHtml: data.internalHtml ?? '',
  externalHtml: data.externalHtml ?? '',
  externalAudience: data.externalAudience,
});

/** Parent must key this body by the immutable organization/mailbox target. */
export function MailboxAutomaticRepliesBody<T extends AutomaticReplies>({ adapter, capabilities, dateTimePicker: DateTimePicker, onSaved }: {
  adapter: MailboxAutomaticRepliesAdapter<T>;
  capabilities: { write: boolean; schedule: 'editable' | 'serverLocalReadOnly' | 'serverLocal' };
  dateTimePicker?: ComponentType<DateTimePickerProps>;
  onSaved?: (data: T) => void;
}) {
  const { t } = useI18n();
  const errors = useErrorText();
  const state = useAsync(() => adapter.load());
  const [form, setForm] = useState<Form>();
  const [saved, setSaved] = useState<T>();
  const [scheduleError, setScheduleError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('schedule');
  const loaded = saved ?? state.data;
  // The editors are uncontrolled: they mount once the data is there, so their default is the stored reply.
  const current = form ?? (loaded ? toForm(loaded) : undefined);

  const pending = useRef(false);
  const dirty = current !== undefined && loaded !== undefined && JSON.stringify(current) !== JSON.stringify(toForm(loaded));
  useReportEdits(dirty, busy);
  const writable = capabilities.write && !(capabilities.schedule === 'serverLocalReadOnly' && current?.state === 'scheduled');
  const nativeSchedule = nativeBounds(loaded);
  if (state.error && !loaded) return <ErrorState description={errors.describe(state.error)} onRetry={state.reload} />;

  const update = (patch: Partial<Form>) => {
    if (busy || !capabilities.write) return;
    setScheduleError(undefined);
    setForm((value) => {
      const base = value ?? (loaded ? toForm(loaded) : undefined);
      return base ? { ...base, ...patch } : value;
    });
  };

  const chooseState = (next: State) => {
    if (busy || !capabilities.write || (next === 'scheduled' && capabilities.schedule === 'serverLocalReadOnly')) return;
    if (next === 'scheduled' && capabilities.schedule === 'editable' && current && !current.start) {
      const start = Math.ceil(Date.now() / HOUR_MS) * HOUR_MS;
      update({ state: next, start: toLocalInput(new Date(start).toISOString()), end: toLocalInput(new Date(start + 24 * HOUR_MS).toISOString()) });
      return;
    }
    update({ state: next });
  };

  const save = async () => {
    if (!current || !writable || pending.current) return;
    const start = capabilities.schedule === 'editable' ? fromLocalInput(current.start) : null;
    const end = capabilities.schedule === 'editable' ? fromLocalInput(current.end) : null;
    const wallTime = (value: string) => value.length === 16 ? `${value.replace('T', ' ')}:00` : value.replace('T', ' ');
    const invalidSchedule = (message: string) => { setTab('schedule'); setScheduleError(message); };
    if (current.state === 'scheduled' && capabilities.schedule === 'serverLocal') {
      if (!current.start || !current.end) return invalidSchedule(t('webmail.automaticReplies.scheduleRequired'));
      if (wallTime(current.end) <= wallTime(current.start)) return invalidSchedule(t('webmail.automaticReplies.scheduleInvalid'));
    }
    if (current.state === 'scheduled' && capabilities.schedule === 'editable') {
      if (!start || !end) return invalidSchedule(t('webmail.automaticReplies.scheduleRequired'));
      if (Date.parse(end) <= Date.parse(start)) return invalidSchedule(t('webmail.automaticReplies.scheduleInvalid'));
    }
    pending.current = true;
    setBusy(true);
    try {
      const result = await adapter.save({
        ...('nativeStartTime' in (loaded ?? {}) ? { internalSubject: loaded?.internalSubject ?? null, externalSubject: loaded?.externalSubject ?? null } : {}),
        state: current.state,
        ...(current.state === 'scheduled' && capabilities.schedule === 'serverLocal' ? {
          scheduleTimeBasis: 'serverLocal' as const, nativeStartTime: wallTime(current.start), nativeEndTime: wallTime(current.end),
        } : {}),
        start: current.state === 'scheduled' ? start : null,
        end: current.state === 'scheduled' ? end : null,
        internalHtml: current.internalHtml,
        externalHtml: current.externalHtml,
        externalAudience: current.externalAudience,
      });
      setSaved(result);
      setForm(undefined);
      onSaved?.(result);
      toast.success(t('webmail.automaticReplies.saved'));
    } catch (error) {
      errors.notify(error);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };

  const audienceOptions: { value: Audience; label: string }[] = [
    { value: 'none', label: t('webmail.automaticReplies.audienceNone') },
    { value: 'known', label: t('webmail.automaticReplies.audienceKnown') },
    { value: 'all', label: t('webmail.automaticReplies.audienceAll') },
  ];

  return (
    <>
        {!current ? (
          <div className="flex flex-col gap-2" aria-busy="true">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : (
          <>
            <Tabs value={tab} onValueChange={setTab}><TabsList aria-label={t('webmail.automaticReplies.stateTitle')}><TabsTrigger value="schedule" invalid={scheduleError !== undefined}>{t('webmail.automaticReplies.stateTitle')}</TabsTrigger><TabsTrigger value="internal">{t('webmail.automaticReplies.internalTitle')}</TabsTrigger><TabsTrigger value="external">{t('webmail.automaticReplies.externalTitle')}</TabsTrigger></TabsList>
            <TabsContent value="schedule">
            <SettingsCard title={t('webmail.automaticReplies.stateTitle')} description={capabilities.schedule === 'editable' ? t('webmail.automaticReplies.stateHint') : undefined}>
              <SegmentedControl
                label={t('webmail.automaticReplies.stateLabel')}
                value={current.state}
                onChange={chooseState}
                disabled={busy || !capabilities.write}
                options={[
                  { value: 'off', label: t('webmail.automaticReplies.stateOff') },
                  { value: 'on', label: t('webmail.automaticReplies.stateOn') },
                  ...(capabilities.schedule !== 'serverLocalReadOnly' || current.state === 'scheduled' ? [{ value: 'scheduled' as const, label: t('webmail.automaticReplies.stateScheduled') }] : []),
                ]}
              />
              {current.state === 'scheduled' && capabilities.schedule === 'serverLocalReadOnly' && loaded && (
                <Alert variant="warning">
                  <TriangleAlertIcon aria-hidden />
                  <div className="flex flex-col gap-2">
                    <p>{t('admin.mailboxSettings.scheduleReadOnly')}</p>
                    <p>{t('admin.mailboxSettings.activityUnknown')}</p>
                    <p>{t('admin.mailboxSettings.nativeStart', { value: nativeSchedule?.nativeStartTime ?? t('status.unknown') })}</p>
                    <p>{t('admin.mailboxSettings.nativeEnd', { value: nativeSchedule?.nativeEndTime ?? t('status.unknown') })}</p>
                  </div>
                </Alert>
              )}
              {capabilities.schedule === 'serverLocal' && <p className="text-sm text-muted-foreground">{t('admin.mailboxSettings.serverLocalSchedule')}</p>}
              {current.state === 'scheduled' && capabilities.schedule !== 'serverLocalReadOnly' && DateTimePicker && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="oof-start">{t('webmail.automaticReplies.start')}</Label>
                    <DateTimePicker id="oof-start" mode="datetime" disabled={busy || !capabilities.write} value={current.start} onValueChange={(value) => update({ start: value })} aria-invalid={scheduleError !== undefined} />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="oof-end">{t('webmail.automaticReplies.end')}</Label>
                    <DateTimePicker id="oof-end" mode="datetime" disabled={busy || !capabilities.write} value={current.end} min={current.start || undefined} onValueChange={(value) => update({ end: value })} aria-invalid={scheduleError !== undefined} />
                  </div>
                  {scheduleError && <p className="text-sm text-destructive sm:col-span-2">{scheduleError}</p>}
                </div>
              )}
            </SettingsCard>
            </TabsContent>

            <fieldset disabled={busy || !writable} inert={busy} className="contents">
            <TabsContent value="internal">
            <SettingsCard title={t('webmail.automaticReplies.internalTitle')} description={t('webmail.automaticReplies.internalDescription')}>
              {!writable ? <SignaturePreview html={current.internalHtml} /> : <LazyRichTextEditor
                label={t('webmail.automaticReplies.internalLabel')}
                key={loaded?.internalHtml ?? ''}
                defaultValue={{ format: 'html', content: current.internalHtml }}
                onChange={(value) => update({ internalHtml: toHtml(value) })}
                className="h-48"
              />}
            </SettingsCard>
            </TabsContent>

            <TabsContent value="external">
            <SettingsCard title={t('webmail.automaticReplies.externalTitle')} description={t('webmail.automaticReplies.externalDescription')}>
              <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-4">
                <Label className="sm:w-56">{t('webmail.automaticReplies.audienceLabel')}</Label>
                <MenuSelect
                  label={t('webmail.automaticReplies.audienceLabel')}
                  value={current.externalAudience}
                  options={audienceOptions}
                  onChange={(value) => update({ externalAudience: value })}
                  disabled={busy || !writable}
                  className="w-64 max-w-full"
                />
              </div>
              {current.externalAudience !== 'none' && (
                !writable ? <SignaturePreview html={current.externalHtml} /> : <LazyRichTextEditor
                  label={t('webmail.automaticReplies.externalLabel')}
                  key={loaded?.externalHtml ?? ''}
                  defaultValue={{ format: 'html', content: current.externalHtml }}
                  onChange={(value) => update({ externalHtml: toHtml(value) })}
                  className="h-48"
                />
              )}
            </SettingsCard>
            </TabsContent>

            </fieldset>
            </Tabs>
            {saved && saved.externalAudienceApplied === false && (
              <Alert variant="warning">
                <TriangleAlertIcon aria-hidden />
                <span>{t('webmail.automaticReplies.audienceNotApplied')}</span>
              </Alert>
            )}
            <div>
              <Button onClick={() => void save()} disabled={busy || !writable}>
                {t('common.save')}
              </Button>
            </div>
          </>
        )}

    </>
  );
}

